import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Modal } from "antd";
import { Loader2 } from "lucide-react";
import type { Agent, AgentInput } from "../api";
import { AGENT_ICONS } from "./AgentIcon";

interface Props {
  open: boolean;
  agent: Agent | null; // null = create
  readOnly?: boolean;
  onClose: () => void;
  onSubmit: (data: AgentInput) => Promise<void>;
}

const NAME_MAX = 80;
const DESCRIPTION_MAX = 300;

const inputClass =
  "w-full rounded-lg border border-border-default-t bg-base px-3 py-2 text-[13px] text-primary-t placeholder:text-tertiary-t focus:border-adrsta-blue focus:outline-none disabled:opacity-70";

function Field({ label, hint, counter, children }: { label: string; hint?: string; counter?: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <label className="text-[13px] font-semibold text-primary-t">{label}</label>
        {counter && <span className="text-xs text-tertiary-t">{counter}</span>}
      </div>
      {children}
      {hint && <p className="mt-1.5 text-xs text-tertiary-t">{hint}</p>}
    </div>
  );
}

export default function AgentFormModal({ open, agent, readOnly, onClose, onSubmit }: Props) {
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("bot");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(agent?.name ?? "");
    setIcon(agent?.icon ?? "bot");
    setDescription(agent?.description ?? "");
    setInstructions(agent?.instructions ?? "");
    setError(null);
  }, [open, agent]);

  const title = readOnly ? agent?.name : agent ? "Edit agent" : "Create agent";
  const subtitle = readOnly
    ? "Built-in agents are read-only. Duplicate this agent to customize it."
    : "Give your agent a name and tell it what analysis to run.";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        icon,
        description: description.trim(),
        instructions: instructions.trim(),
        // Not editable here; values on existing agents (e.g. duplicated built-ins) are kept as-is.
        defaultParams: agent?.defaultParams ?? {},
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the agent.");
    } finally {
      setSaving(false);
    }
  };

  const canSubmit = name.trim() !== "" && instructions.trim() !== "" && !saving;

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={600} destroyOnHidden centered maskClosable={false}>
      <form onSubmit={handleSubmit} className="pt-1">
        <div className="mb-5 pr-6">
          <h2 className="text-lg font-semibold text-primary-t">{title}</h2>
          <p className="mt-1 text-[13px] text-secondary-t">{subtitle}</p>
        </div>

        <div className="max-h-[62vh] space-y-5 overflow-y-auto pr-1">
          <Field label="Name" counter={readOnly ? undefined : `${name.length}/${NAME_MAX}`}>
            <input
              className={inputClass}
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={NAME_MAX}
              required
              disabled={readOnly}
              placeholder="e.g. Campaign Performance Analyst"
            />
          </Field>

          <Field
            label="Description"
            hint="Shown on the agent card."
            counter={readOnly ? undefined : `${description.length}/${DESCRIPTION_MAX}`}
          >
            <input
              className={inputClass}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={DESCRIPTION_MAX}
              disabled={readOnly}
              placeholder="What this agent helps with, in one line"
            />
          </Field>

          <Field label="Icon">
            <div className="grid grid-cols-10 gap-1.5">
              {Object.entries(AGENT_ICONS).map(([key, Icon]) => {
                const selected = icon === key;
                return (
                  <button
                    key={key}
                    type="button"
                    title={key}
                    aria-pressed={selected}
                    disabled={readOnly}
                    onClick={() => setIcon(key)}
                    className={`flex aspect-square items-center justify-center rounded-lg border transition disabled:cursor-default ${
                      selected
                        ? "border-adrsta-blue text-adrsta-blue"
                        : "border-border-default-t text-tertiary-t hover:bg-hover-bg hover:text-primary-t"
                    }`}
                    style={selected ? { background: "var(--blue-subtle)" } : undefined}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </button>
                );
              })}
            </div>
          </Field>

          <Field
            label="Instructions"
            hint="Include the KPI, date range, and any assumptions the agent should use by default."
          >
            <textarea
              className={`${inputClass} min-h-[180px] resize-y leading-relaxed`}
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              required
              disabled={readOnly}
              placeholder="Describe the agent's role, the analysis it should run, and how to present the results."
            />
          </Field>

          {error && (
            <p className="rounded-lg px-3 py-2 text-[13px]" style={{ background: "var(--error-subtle)", color: "var(--error)" }}>
              {error}
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-3 border-t border-border-subtle-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-default-t px-4 py-2 text-[13px] font-semibold text-secondary-t hover:bg-hover-bg"
          >
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex items-center gap-2 rounded-lg bg-adrsta-blue px-4 py-2 text-[13px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saving ? "Saving…" : agent ? "Save changes" : "Create agent"}
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
